import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { Maximize2, Minimize2, Play, Plus, X } from 'lucide-react';
import { auth } from '../../config/firebase';
import { db } from '../../config/firebase';
import useAdminStore from '../../store/adminStore';
import useAppStore from '../../store/appStore';
import type { VideoTutorialSettings } from '../../store/types';
import { resolveTeamMemberByEmail } from '../../utils/teamAuth';
import {
    buildAutoplayEmbedUrl,
    buildVimeoPublicUrl,
    extractVimeoId,
    extractVimeoPrivacyHash,
    fetchVimeoPosterUrl,
    getVimeoPosterUrl,
    getYoutubePosterUrl,
    normalizeEmbedSrc,
} from '../../utils/videoEmbed';

export interface PageVideoHelpProps {
    pageKey: string;
    className?: string;
    buttonLabel?: string;
}

const PageVideoHelp: React.FC<PageVideoHelpProps> = ({
    pageKey,
    className = '',
    buttonLabel = 'Video-Anleitung',
}) => {
    const impersonatedClientId = useAdminStore((s) => s.impersonatedClientId);
    const team = useAppStore((s) => s.team);
    const teamRegistry = useAppStore((s) => s.teamRegistry);
    const settings = useAppStore((s) => s.settings);
    const updateSettings = useAppStore((s) => s.updateSettings);

    const email = auth.currentUser?.email || null;
    const teamInfo = useMemo(
        () => resolveTeamMemberByEmail(email, team, teamRegistry),
        [email, team, teamRegistry]
    );

    const godSessionId = (() => {
        try {
            return typeof sessionStorage !== 'undefined'
                ? sessionStorage.getItem('GOD_MODE_ID')
                : null;
        } catch {
            return null;
        }
    })();

    const peAdminUnlocked = (() => {
        try {
            return typeof sessionStorage !== 'undefined'
                ? sessionStorage.getItem('PE_ADMIN_UNLOCKED') === '1'
                : false;
        } catch {
            return false;
        }
    })();

    // Wasserdicht: Impersonation OR Session-Mirror OR Team (Coach/Admin) OR Unlock-Flag
    const isGodMode =
        !!impersonatedClientId ||
        !!godSessionId ||
        peAdminUnlocked ||
        teamInfo.isTeam === true;

    const tutorialVideos: VideoTutorialSettings[] = useMemo(
        () => (Array.isArray(settings?.tutorialVideos) ? settings.tutorialVideos : []),
        [settings?.tutorialVideos]
    );
    const pageVideoHelp: Record<string, string> = useMemo(
        () =>
            settings?.pageVideoHelp && typeof settings.pageVideoHelp === 'object'
                ? settings.pageVideoHelp
                : {},
        [settings?.pageVideoHelp]
    );

    const assignedVideoId = pageVideoHelp[pageKey] || '';
    const currentVideo = tutorialVideos.find((v) => v.id === assignedVideoId) || null;

    const [open, setOpen] = useState(false);
    const [cinema, setCinema] = useState(false);
    const [coachOpen, setCoachOpen] = useState(false);
    const [draftVideoId, setDraftVideoId] = useState(assignedVideoId);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [posterUrl, setPosterUrl] = useState<string | null>(null);

    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('[PageVideoHelp Debug]', {
            pageKey,
            isGodMode,
            impersonatedClientId,
            godSessionId,
            peAdminUnlocked,
            teamIsTeam: teamInfo.isTeam,
            teamRole: teamInfo.role,
            email,
            assignedVideoId: settings?.pageVideoHelp?.[pageKey] || null,
            willRender: !!(currentVideo || isGodMode),
        });
    }, [
        pageKey,
        isGodMode,
        impersonatedClientId,
        godSessionId,
        peAdminUnlocked,
        teamInfo.isTeam,
        teamInfo.role,
        email,
        settings?.pageVideoHelp,
        currentVideo,
    ]);

    useEffect(() => {
        if (open) {
            setDraftVideoId(assignedVideoId);
            setCoachOpen(isGodMode && !currentVideo);
            setCinema(false);
            setSaveMsg(null);
            setIsPlaying(false);
        }
    }, [open, assignedVideoId, isGodMode, currentVideo]);

    // Poster wie Academy — Privacy-Hash-Videos brauchen oft User-Gesture vor dem iframe
    useEffect(() => {
        if (!open || !currentVideo?.src) {
            setPosterUrl(null);
            return;
        }
        let cancelled = false;
        const raw = normalizeEmbedSrc(currentVideo.src);
        const yt = getYoutubePosterUrl(raw);
        if (yt) {
            setPosterUrl(yt);
            return;
        }
        // Sofort wie PEBase (vumbnail), oEmbed optional danach
        setPosterUrl(getVimeoPosterUrl(raw));
        fetchVimeoPosterUrl(raw).then((url) => {
            if (!cancelled && url) setPosterUrl(url);
        });
        return () => {
            cancelled = true;
        };
    }, [open, currentVideo?.src]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const rawSrc = currentVideo?.src ? normalizeEmbedSrc(currentVideo.src) : '';
    const vimeoId = rawSrc ? extractVimeoId(rawSrc) : null;
    const privacyHash = rawSrc ? extractVimeoPrivacyHash(rawSrc) : null;
    const playerSrc = rawSrc ? buildAutoplayEmbedUrl(rawSrc) : '';
    const vimeoWatchUrl =
        vimeoId ? buildVimeoPublicUrl(vimeoId, privacyHash) : rawSrc || '';

    useEffect(() => {
        if (!open || !currentVideo) return;
        // eslint-disable-next-line no-console
        console.log('[PageVideoHelp Embed]', {
            pageKey,
            title: currentVideo.title,
            rawSrc,
            vimeoId,
            privacyHash,
            playerSrc,
        });
    }, [open, currentVideo, pageKey, rawSrc, vimeoId, privacyHash, playerSrc]);

    // Fall 3: Mandant ohne Video → nichts rendern
    if (!currentVideo && !isGodMode) {
        return null;
    }

    const handleSaveGlobal = async () => {
        const next: Record<string, string> = { ...pageVideoHelp };
        if (!draftVideoId) {
            delete next[pageKey];
        } else {
            next[pageKey] = draftVideoId;
        }

        setSaving(true);
        setSaveMsg(null);
        try {
            updateSettings('pageVideoHelp' as any, next);
            const sysRef = doc(db, 'system_data', 'global_settings');
            await updateDoc(sysRef, { 'settings.pageVideoHelp': next });
            setSaveMsg({ type: 'ok', text: 'Video-Zuweisung global gespeichert.' });
            setCoachOpen(false);
            if (!draftVideoId) setOpen(false);
        } catch (err: any) {
            console.error('[PageVideoHelp] Global save failed', err);
            setSaveMsg({
                type: 'err',
                text: err?.message
                    ? `Speichern fehlgeschlagen: ${err.message}`
                    : 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
            });
        } finally {
            setSaving(false);
        }
    };

    const triggerButton = currentVideo ? (
        <button
            type="button"
            onClick={() => setOpen(true)}
            className={`inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-wide sm:tracking-widest transition-all border bg-[#1E293B] text-[#F8FAFC] border-slate-600 hover:border-[#E6C299]/60 hover:text-[#E6C299] cursor-pointer whitespace-nowrap ${className}`}
            title={currentVideo.title || buttonLabel}
        >
            <Play className="w-3.5 h-3.5 text-[#E6C299] fill-[#E6C299] shrink-0" />
            <span className="truncate">{buttonLabel}</span>
        </button>
    ) : (
        <button
            type="button"
            onClick={() => setOpen(true)}
            className={`border-2 border-dashed border-[#DCB68A] text-[#DCB68A] bg-[#1E293B] px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-[#DCB68A] hover:text-black transition-all cursor-pointer inline-flex items-center gap-2 ${className}`}
            title="Academy-Video dieser Seite zuweisen (Gott-Modus)"
        >
            <Plus className="w-3.5 h-3.5" />
            [ + Video zuweisen ]
        </button>
    );

    return (
        <>
            {triggerButton}

            {open &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[9998] flex items-center justify-center p-4 md:p-8"
                        role="dialog"
                        aria-modal="true"
                        aria-label={currentVideo?.title || 'Video-Hilfe'}
                    >
                        <div
                            className="absolute inset-0 bg-[#0A0A0A]/80 backdrop-blur-[2px]"
                            onClick={() => setOpen(false)}
                        />
                        <div
                            className={`relative z-10 w-full flex flex-col rounded-2xl border border-slate-600 bg-[#1E293B] shadow-2xl overflow-hidden transition-all ${
                                cinema ? 'max-w-6xl max-h-[95vh]' : 'max-w-3xl max-h-[90vh]'
                            }`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-600/80 shrink-0">
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#E6C299] mb-1">
                                        Seitenhilfe
                                    </p>
                                    <h3 className="text-base md:text-lg font-bold text-[#F8FAFC] truncate">
                                        {currentVideo?.title || 'Video zuweisen'}
                                    </h3>
                                    {currentVideo?.description ? (
                                        <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                                            {currentVideo.description}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {currentVideo ? (
                                        <button
                                            type="button"
                                            onClick={() => setCinema((v) => !v)}
                                            className="p-2 rounded-lg border border-slate-600 text-slate-300 hover:text-[#E6C299] hover:border-[#E6C299]/50 transition-colors"
                                            title={cinema ? 'Normalansicht' : 'Kino-Modus'}
                                            aria-label={cinema ? 'Normalansicht' : 'Kino-Modus'}
                                        >
                                            {cinema ? (
                                                <Minimize2 className="w-4 h-4" />
                                            ) : (
                                                <Maximize2 className="w-4 h-4" />
                                            )}
                                        </button>
                                    ) : null}
                                    <button
                                        type="button"
                                        onClick={() => setOpen(false)}
                                        className="p-2 rounded-lg border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 transition-colors"
                                        aria-label="Schließen"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <div className="overflow-y-auto flex-1 min-h-0">
                                {currentVideo && rawSrc ? (
                                    <div className="relative w-full aspect-video bg-black border-b border-slate-700">
                                        {isPlaying && playerSrc ? (
                                            <iframe
                                                key={playerSrc}
                                                className="absolute inset-0 w-full h-full border-0"
                                                src={playerSrc}
                                                title={currentVideo.title || 'Video'}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                                allowFullScreen
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setIsPlaying(true)}
                                                className="absolute inset-0 w-full h-full border-0 p-0 cursor-pointer bg-slate-900"
                                                aria-label={`${currentVideo.title || 'Video'} abspielen`}
                                            >
                                                {posterUrl ? (
                                                    <img
                                                        src={posterUrl}
                                                        alt=""
                                                        className="absolute inset-0 w-full h-full object-cover"
                                                        referrerPolicy="no-referrer"
                                                    />
                                                ) : (
                                                    <span className="absolute inset-0 bg-slate-900" />
                                                )}
                                                <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                                                    <span className="w-16 h-16 rounded-full bg-[#E6C299] text-black flex items-center justify-center shadow-lg">
                                                        <Play className="w-7 h-7 ml-0.5 fill-black" />
                                                    </span>
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="px-5 py-10 text-center text-slate-400 text-sm border-b border-slate-700">
                                        Noch kein Academy-Video zugewiesen.
                                        {isGodMode
                                            ? ' Wähle unten ein Video aus dem Katalog.'
                                            : ''}
                                    </div>
                                )}
                                {currentVideo && vimeoWatchUrl ? (
                                    <div className="px-5 py-2 border-b border-slate-700/80 bg-slate-900/40 flex flex-wrap items-center justify-between gap-2">
                                        <p className="text-[10px] text-slate-500">
                                            Falls der Player nicht lädt: Vimeo-Domain-Freigabe prüfen
                                            (localhost / pe-one-app.web.app).
                                        </p>
                                        <a
                                            href={vimeoWatchUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[10px] font-bold uppercase tracking-widest text-[#E6C299] hover:underline"
                                        >
                                            Auf Vimeo öffnen ↗
                                        </a>
                                    </div>
                                ) : null}

                                {isGodMode && (
                                    <div className="px-5 py-4 bg-slate-900/50 border-t border-slate-700/60">
                                        <button
                                            type="button"
                                            onClick={() => setCoachOpen((v) => !v)}
                                            className="text-[10px] font-black uppercase tracking-widest text-[#E6C299] hover:text-[#f0d3b3] transition-colors"
                                        >
                                            {coachOpen
                                                ? '▾ Coach-Zuweisung ausblenden'
                                                : '▸ Coach: Video zuweisen / ändern'}
                                        </button>

                                        {coachOpen && (
                                            <div className="mt-4 space-y-3 animate-fadeIn">
                                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                    Academy-Video für „{pageKey}“
                                                </label>
                                                <select
                                                    value={draftVideoId}
                                                    onChange={(e) => setDraftVideoId(e.target.value)}
                                                    className="w-full rounded-xl border border-slate-600 bg-[#0f172a] text-[#F8FAFC] text-sm px-3 py-2.5 outline-none focus:border-[#E6C299]/60"
                                                >
                                                    <option value="">Kein Video (Deaktivieren)</option>
                                                    {tutorialVideos.map((v) => (
                                                        <option key={v.id} value={v.id}>
                                                            {v.title || 'Ohne Titel'} · ID {v.id}
                                                            {v.isPublic === false
                                                                ? ' · (nicht Academy-öffentlich)'
                                                                : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                                {tutorialVideos.length === 0 && (
                                                    <p className="text-xs text-amber-400/90">
                                                        Keine Videos im Katalog. Bitte zuerst unter
                                                        Kalkulationsdaten → Video-Tutorials anlegen.
                                                    </p>
                                                )}
                                                <button
                                                    type="button"
                                                    disabled={saving}
                                                    onClick={handleSaveGlobal}
                                                    className="w-full md:w-auto px-5 py-2.5 rounded-xl bg-[#E6C299] text-[#0f172a] text-xs font-black uppercase tracking-widest hover:bg-[#d4ac7d] disabled:opacity-60 transition-colors"
                                                >
                                                    {saving
                                                        ? 'Speichern…'
                                                        : 'Speichern & Global aktivieren'}
                                                </button>
                                                {saveMsg && (
                                                    <p
                                                        className={`text-xs ${
                                                            saveMsg.type === 'ok'
                                                                ? 'text-emerald-400'
                                                                : 'text-red-400'
                                                        }`}
                                                    >
                                                        {saveMsg.text}
                                                    </p>
                                                )}
                                                <p className="text-[10px] text-slate-500 leading-relaxed">
                                                    Schreibt salonübergreifend nach{' '}
                                                    <code className="text-slate-400">
                                                        system_data/global_settings.settings.pageVideoHelp
                                                    </code>
                                                    .
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default PageVideoHelp;
